// src/utils/axios.js
import axios from 'axios';
import { config } from 'src/config-global';
import {
  attachCsrfGate,
  applyCsrfHeader,
  getCsrfToken,
  refreshCsrf,
  CSRF_HEADER,
  CSRF_PATH,
} from 'src/utils/csrf';

const axiosInstanceNew = axios.create({
  withCredentials: true,
  baseURL: config.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── CSRF token attachment interceptor ─────────────────────────────────────
// Reads the in-memory token on every request and sets the x-csrf-token
// header. Skips the CSRF fetch route itself.
export function attachCsrf(instance = axiosInstanceNew) {
  if (instance._csrfAttached) return;
  instance._csrfAttached = true;

  instance.interceptors.request.use((req) => {
    if (req.url?.includes(CSRF_PATH)) return req;
    return applyCsrfHeader(req);
  });
}

// ── CSRF self-recovery ────────────────────────────────────────────────────
// When the server rotates the session (login, wallet claim, session expiry)
// the token we hold becomes stale and every write fails with 403. Instead of
// leaving the app broken until a hard refresh, fetch a fresh token once and
// replay the failed request.
function isCsrfRejection(error) {
  const status = error?.response?.status;
  if (status !== 403) return false;

  const data = error?.response?.data || {};
  const text = `${data.code || ''} ${data.message || ''} ${data.error || ''}`.toLowerCase();

  return text.includes('csrf');
}

function readSentToken(requestConfig) {
  const headers = requestConfig?.headers;
  if (!headers) return null;

  if (typeof headers.get === 'function') {
    return headers.get(CSRF_HEADER) || null;
  }

  return headers[CSRF_HEADER] || null;
}

function attachCsrfRetry(instance) {
  if (instance._csrfRetryAttached) return;
  instance._csrfRetryAttached = true;

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;

      if (!original || original._csrfRetried || original._skipCsrfRetry) {
        return Promise.reject(error);
      }

      if (String(original.url || '').includes(CSRF_PATH)) {
        return Promise.reject(error);
      }

      if (!isCsrfRejection(error)) {
        return Promise.reject(error);
      }

      original._csrfRetried = true;

      const sentToken = readSentToken(original);
      const currentToken = getCsrfToken();

      // Another request already refreshed the token after this one was sent:
      // just replay with the new token.
      if (currentToken && currentToken !== sentToken) {
        applyCsrfHeader(original);
        return instance(original);
      }

      try {
        await refreshCsrf(instance);
      } catch (refreshError) {
        console.error('[csrf] token refresh after 403 failed', refreshError);
        return Promise.reject(error);
      }

      applyCsrfHeader(original);

      return instance(original);
    }
  );
}

// ── Module initialisation ─────────────────────────────────────────────────
// attachCsrfGate — pauses requests until a token exists (bounded wait).
// attachCsrf     — stamps the current token on each request.
// attachCsrfRetry — refreshes the token and replays once on a CSRF 403.
attachCsrfGate(axiosInstanceNew);
attachCsrf(axiosInstanceNew);
attachCsrfRetry(axiosInstanceNew);

export { axios, axiosInstanceNew };
